import { useMemo, useState } from 'react'
import { Button } from '../ui'
import { ApiError } from '../../api/client'
import { useLocationTree, useMappingDecide, usePersons } from '../../api/hooks'
import { locationPaths } from '../../lib/locations'
import type { AccessSystem, LocationNode, MappingItem } from '../../api/types'
import { CREDENTIAL_KINDS } from '../../pages/Credentials'
import { DOOR_KIND_LABELS, candidateDoorKind } from '../../pages/Doors'
import type { DoorKind } from '../../api/types'
import { ScopePicker } from './ScopePicker'
import { isSightedCandidate } from './MappingWorkbench'
import { kindLabels } from './MappingCard'
import { LocationLink } from '../LocationLink'

/** Kinds whose backend adopt() refuses action="create": acre zones/devices
 *  are attach-only (connectors/acre/adoption.py raises
 *  CapabilityNotSupported on mode="create" -> 400 capability_not_supported).
 *  The Create button is hidden for these — the operator can only attach. */
const attachOnlyKinds: Record<string, ReadonlySet<string>> = {
  acre: new Set(['zone', 'device']),
}

export function canCreate(provider: string, kind: string): boolean {
  return !attachOnlyKinds[provider]?.has(kind)
}

/** Providers whose `location` kind creates a directory.Group, not a Location
 *  (techfass folders / axema org groups adopt as Groups — the kind stays
 *  `location` on the API surface, but Location(type='company') is gone).
 *  The create label must name what the backend actually creates. */
const groupCreateProviders: ReadonlySet<string> = new Set(['techfass', 'axema'])

function createLabel(provider: string, kind: string): string {
  if (kind === 'location' && groupCreateProviders.has(provider)) return 'group'
  // kindLabels is pluralized for the rail; a single create needs the singular.
  if (kind === 'access_group') return 'access group'
  if (kind === 'schedule') return 'schedule'
  if (kind === 'zone') return 'zone'
  if (kind === 'credential') return 'credential'
  if (kind === 'door') return 'door'
  return (kindLabels[kind] ?? kind).toLowerCase()
}

/** Providers that bundle the person's own credentials on a person create
 *  adopt (techfass _bundle_credentials_for, axema _bundle_token_for) —
 *  the only places `fields.include_credentials` means anything. */
export const credentialBundleProviders: ReadonlySet<string> = new Set(['techfass', 'axema'])

/** CAME reads `fields.location` (a Location uid) on device/person decisions
 *  — it places the device or attaches the person's intercom presence.
 *  (door_code outgrew the single field: its create takes the union scope,
 *  see below.) Other connectors ignore it. */
const locationFieldKinds: ReadonlySet<string> = new Set(['device', 'person'])

/** Axema reads an OPTIONAL `fields.location` on door create (the new Door is
 *  hung there; unset = the door's vendor placement, 400 invalid_vendor_data
 *  when the vendor row carries none). */
const optionalLocationFieldKinds: Record<string, ReadonlySet<string>> = {
  axema: new Set(['door']),
}

/** Whether the create form collects `fields.location` for this provider/kind
 *  (CAME device/person/door_code; optional on axema door). */
export function hasLocationField(provider: string, kind: string): boolean {
  return (
    (provider === 'came' && locationFieldKinds.has(kind)) ||
    (optionalLocationFieldKinds[provider]?.has(kind) ?? false)
  )
}

/** A TECHFASS door candidate takes `fields.locations` (a Location list, the
 *  first entry wins) — REQUIRED when the reader is unplaced
 *  (`detail.location_path` empty; the backend UnknownCandidates without a
 *  scope), optional when placed (the backend falls back to the device's
 *  placement). came door candidates are placed-only and their adopt reads
 *  no door fields, so this gating is techfass-only. */
export function techfassDoorNeedsLocations(system: AccessSystem, item: MappingItem): boolean {
  return system.provider === 'techfass' && item.kind === 'door'
}

/** Whether a door candidate's device already carries a placement
 *  (`detail.location_path` is the breadcrumb string, '' when unplaced;
 *  defensive when absent). */
export function doorPlaced(item: MappingItem): boolean {
  return typeof item.detail.location_path === 'string' && item.detail.location_path !== ''
}

/** A door_code candidate's seen-on doors: `detail.doors` carries
 *  [{uid, name, breadcrumb?}] (devices placed on those doors) — they
 *  pre-select the create form's door scope and feed the one-click create.
 *  Defensive: absent/malformed detail = no doors (the backend treats no
 *  scope as "no doors yet"). */
export function seenOnDoorUids(item: MappingItem): string[] {
  const raw = item.detail.doors
  if (!Array.isArray(raw)) return []
  const uids: string[] = []
  for (const d of raw) {
    if (typeof d === 'object' && d !== null && typeof (d as { uid?: unknown }).uid === 'string') {
      uids.push((d as { uid: string }).uid)
    }
  }
  return uids
}

/** The fields a ONE-CLICK create sends: exactly what the form submits
 *  untouched — `include_credentials` on for person creates on the bundling
 *  providers (techfass/axema), the top location hint as `fields.location` on
 *  the location-picker kinds, the seen-on doors as `fields.doors` on a
 *  door_code candidate (no scope when absent); the vendor name and `manage`
 *  stay backend-side defaults. null = the row needs the form: a sighted
 *  new-credential convert (its holder person is required), or a
 *  rotation-detected door_code (one-click must not silently create a
 *  rotating series — the workbench routes the click to the form so the
 *  operator sees what they're creating). */
export function defaultCreateFields(system: AccessSystem, item: MappingItem): Record<string, unknown> | null {
  if (isSightedCandidate(item)) return null
  if (item.detail.rotation) return null
  const fields: Record<string, unknown> = {}
  if (item.kind === 'person' && credentialBundleProviders.has(system.provider)) {
    fields.include_credentials = true
  }
  if (hasLocationField(system.provider, item.kind)) {
    const hint = item.suggestions.find((s) => s.target_kind === 'location')
    if (hint) fields.location = hint.canonical_uid
  }
  if (techfassDoorNeedsLocations(system, item)) {
    // An unplaced techfass reader one-clicks into a Door AT the hinted
    // address (fields.locations, first wins); without a hint the location
    // pick is required — the click routes to the form.
    const hint = item.suggestions.find((s) => s.target_kind === 'location')
    if (hint) fields.locations = [hint.canonical_uid]
    else if (!doorPlaced(item)) return null
  }
  if (item.kind === 'door_code') {
    const doors = seenOnDoorUids(item)
    if (doors.length > 0) fields.doors = doors
  }
  return fields
}

/** The fields a 100% LOCATION-hint quick action ("Create at {name}") sends:
 *  the defaultCreateFields payload with the location FORCED to the hint —
 *  `fields.location` on the single-location kinds (came device/person,
 *  axema door), the union scope on door_code (`fields.locations: [hint]`
 *  plus the seen-on `fields.doors` when present). null when the kind takes
 *  no location input at all or the row needs the form (sighted convert /
 *  rotation-detected door_code — the series toggle must be seen). */
export function createAtFields(
  system: AccessSystem,
  item: MappingItem,
  locationUid: string,
): Record<string, unknown> | null {
  if (isSightedCandidate(item)) return null
  if (item.detail.rotation) return null
  const fields: Record<string, unknown> = {}
  if (item.kind === 'person' && credentialBundleProviders.has(system.provider)) {
    fields.include_credentials = true
  }
  if (hasLocationField(system.provider, item.kind)) {
    fields.location = locationUid
  } else if (techfassDoorNeedsLocations(system, item)) {
    fields.locations = [locationUid]
  } else if (item.kind === 'door_code') {
    const doors = seenOnDoorUids(item)
    if (doors.length > 0) fields.doors = doors
    fields.locations = [locationUid]
  } else {
    return null
  }
  return fields
}

/** The fields a 100% location-hint quick action on a ROTATION-DETECTED
 *  door_code sends ("Create series at {name}"): `fields.series: true` plus
 *  the union scope (`fields.locations: [hint]` + the seen-on
 *  `fields.doors` when present). Rotation rows are excluded from
 *  createAtFields (a silent STATIC create must never happen) — but the
 *  series variant needs no operator judgement, so it fires directly.
 *  null unless the row is a rotation-detected door_code. */
export function createSeriesAtFields(item: MappingItem, locationUid: string): Record<string, unknown> | null {
  if (item.kind !== 'door_code' || !item.detail.rotation) return null
  const fields: Record<string, unknown> = { series: true, locations: [locationUid] }
  const doors = seenOnDoorUids(item)
  if (doors.length > 0) fields.doors = doors
  return fields
}

/** Human labels for the rotation cadences (detail.rotation.cadence). */
const CADENCE_LABELS: Record<string, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semiannual: 'Semiannual',
}

function flattenLocations(nodes: LocationNode[], depth = 0): { uid: string; label: string }[] {
  return nodes.flatMap((n) => [
    {
      uid: n.unique_id,
      label: `${'— '.repeat(depth)}${n.name}${n.inherits_grants === false ? ' 🔒' : ''}`,
    },
    ...flattenLocations(n.children, depth + 1),
  ])
}

function errDetail(e: unknown): string {
  if (e instanceof ApiError) return e.detail
  if (e instanceof Error) return e.message
  return 'Request failed'
}

/** Inline "create new canonical entity" decision for one candidate row.
 *  The backend derives the new entity from the vendor row itself — the only
 *  decision fields any connector's adopt() consumes are
 *  `include_credentials` (person create on techfass/axema), `location`
 *  (CAME device/person, optional on axema door), and the door_code union
 *  scope (`doors` + `locations`, the seen-on doors pre-selected) — so the
 *  form collects exactly those.
 *  On success the decide mutation invalidates the scope and the board
 *  refetches with the row bound. */
export function MappingCreateForm({
  system,
  item,
  site,
  initialLocationUid,
  onDone,
}: {
  system: AccessSystem
  item: MappingItem
  /** The workbench's selected site — the decide mutation patches the
   *  site-scoped board cache. */
  site?: string
  /** Pre-set location picker uid (a clicked location suggestion) — takes
   *  precedence over the automatic top-hint prefill. */
  initialLocationUid?: string
  onDone: () => void
}) {
  const decide = useMappingDecide(system.unique_id, site)
  const locationTree = useLocationTree()
  const persons = usePersons()

  // Sighted unknown-credential rows (new IDs) convert: a holder Person is
  // REQUIRED backend-side, the kind defaults to card.
  const sighted = isSightedCandidate(item)
  const showIncludeCredentials = item.kind === 'person' && credentialBundleProviders.has(system.provider)
  const showLocation = hasLocationField(system.provider, item.kind)
  /** The candidate's best LOCATION hint (the backend returns suggestions
   *  ranked, so the first location-kind hint wins) pre-fills the location
   *  picker — e.g. a CAME panel named after its address. A pre-fill, never
   *  a lock: the operator can change the picker freely. */
  const locationHint = showLocation
    ? (item.suggestions.find((s) => s.target_kind === 'location') ?? null)
    : null
  /** TECHFASS door: fields.locations picker — REQUIRED when the reader is
   *  unplaced (backend UnknownCandidates without a scope), optional when
   *  placed (defaults to the device placement). The top location hint
   *  pre-fills (the address match); came door candidates are placed-only
   *  and axema keeps its optional single fields.location above. */
  const showDoorLocations = techfassDoorNeedsLocations(system, item)
  const doorLocationRequired = showDoorLocations && !doorPlaced(item)
  const doorLocationHint = showDoorLocations
    ? (item.suggestions.find((s) => s.target_kind === 'location') ?? null)
    : null
  /** door_code create takes the UNION scope (fields.doors + fields.locations,
   *  resolved at-or-under backend-side); the candidate's seen-on doors
   *  pre-select the door side. */
  const showScope = item.kind === 'door_code'
  /** Rotation-detected door_code (the vendor description matched a period
   *  pattern): the create can make a DoorCodeSeries instead of a one-off
   *  static code — the toggle sits above the scope picker, default ON. */
  const rotation = showScope ? (item.detail.rotation ?? null) : null

  const [includeCredentials, setIncludeCredentials] = useState(true)
  const [locationUid, setLocationUid] = useState(initialLocationUid ?? locationHint?.canonical_uid ?? '')
  const [doorLocationUid, setDoorLocationUid] = useState(
    initialLocationUid ?? doorLocationHint?.canonical_uid ?? '',
  )
  const [scopeDoors, setScopeDoors] = useState<string[]>(() => (showScope ? seenOnDoorUids(item) : []))
  // A clicked LOCATION suggestion lands in the scope picker's location side
  // for door_code (it has no single-location field), in the plain picker
  // otherwise.
  const [scopeLocations, setScopeLocations] = useState<string[]>(() =>
    showScope && initialLocationUid ? [initialLocationUid] : [],
  )
  const [asSeries, setAsSeries] = useState(true)
  /** Door create: '' = the backend's detect_door_kind on the device name
   *  (mirrored by doorKindFromName in the option label); an explicit pick
   *  sends fields.kind (all providers). */
  const [doorKindSel, setDoorKindSel] = useState<DoorKind | ''>('')
  const [personUid, setPersonUid] = useState('')
  const [credentialKind, setCredentialKind] = useState('card')

  // access_group/schedule/zone create derives the canonical row's name from
  // the vendor row backend-side (no editable fields) — show it prefilled so
  // the operator sees exactly what will be created.
  const showName = item.kind === 'access_group' || item.kind === 'schedule' || item.kind === 'zone'
  const locations = useMemo(() => flattenLocations(locationTree.data ?? []), [locationTree.data])
  const locationLabels = useMemo(() => locationPaths(locationTree.data ?? []), [locationTree.data])

  const label = createLabel(system.provider, item.kind)

  const submit = () => {
    if (decide.isPending) return
    const fields: Record<string, unknown> = {}
    if (sighted) {
      if (!personUid) return // the holder is required to convert a sighting
      fields.person = personUid
      fields.kind = credentialKind
    }
    if (showIncludeCredentials && includeCredentials) fields.include_credentials = true
    if (showLocation && locationUid) fields.location = locationUid
    if (showDoorLocations) {
      if (doorLocationRequired && !doorLocationUid) return // an unplaced reader needs its location
      if (doorLocationUid) fields.locations = [doorLocationUid]
    }
    if (item.kind === 'door' && doorKindSel !== '') fields.kind = doorKindSel
    if (showScope) {
      if (scopeDoors.length > 0) fields.doors = scopeDoors
      if (scopeLocations.length > 0) fields.locations = scopeLocations
      // Rotating series: the decision's scope becomes the series scope and
      // the new code is linked as the current period (backend rotates it
      // from there). Off = plain static code.
      if (rotation && asSeries) fields.series = true
    }
    decide.mutate(
      {
        kind: item.kind,
        vendor_uid: item.vendor_uid,
        action: 'create',
        fields: Object.keys(fields).length > 0 ? fields : null,
      },
      { onSuccess: onDone },
    )
  }

  return (
    <form
      className="flex flex-col gap-3 rounded-sm border border-ostrel-line bg-ostrel-sand/40 px-3 py-2.5"
      onSubmit={(e) => {
        e.preventDefault()
        submit()
      }}
    >
      <p className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
        {sighted ? 'Convert new ID to credential' : `Create new ${label}`}
      </p>
      {showName && (
        <label className="flex flex-col gap-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
            Name
          </span>
          <input
            type="text"
            value={item.display}
            readOnly
            disabled
            className="rounded-sm border border-ostrel-line bg-ostrel-card px-3 py-2 text-sm text-ostrel-ink-muted"
          />
        </label>
      )}
      {sighted && (
        <>
          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
              Holder
            </span>
            <select
              value={personUid}
              onChange={(e) => setPersonUid(e.target.value)}
              aria-label="Credential holder"
              className="rounded-sm border border-ostrel-line bg-ostrel-card px-3 py-2 text-sm text-ostrel-ink focus:border-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
            >
              <option value="">— select person</option>
              {(persons.data ?? []).map((p) => (
                <option key={p.unique_id} value={p.unique_id}>
                  {p.display_name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
              Kind
            </span>
            <select
              value={credentialKind}
              onChange={(e) => setCredentialKind(e.target.value)}
              aria-label="Credential kind"
              className="rounded-sm border border-ostrel-line bg-ostrel-card px-3 py-2 text-sm text-ostrel-ink focus:border-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
            >
              {CREDENTIAL_KINDS.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </label>
        </>
      )}
      {showIncludeCredentials && (
        <label className="flex items-center gap-2 text-xs text-ostrel-ink">
          <input
            type="checkbox"
            checked={includeCredentials}
            onChange={(e) => setIncludeCredentials(e.target.checked)}
            className="accent-ostrel-gold"
          />
          Also adopt this person's credentials
        </label>
      )}
      {showLocation && (
        <label className="flex flex-col gap-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
            Location (optional)
          </span>
          {locationHint && (
            <p className="tnum text-[11px] text-ostrel-ink-muted">
              Suggested:{' '}
              <span className="text-ostrel-gold">
                <LocationLink
                  uid={locationHint.canonical_uid}
                  className="text-ostrel-gold hover:text-ostrel-accent focus:ring-ostrel-gold"
                >
                  {locationLabels.get(locationHint.canonical_uid) ?? locationHint.canonical_uid}
                </LocationLink>
              </span>{' '}
              · {Math.round(locationHint.score * 100)}% match
            </p>
          )}
          <select
            value={locationUid}
            onChange={(e) => setLocationUid(e.target.value)}
            className="rounded-sm border border-ostrel-line bg-ostrel-card px-3 py-2 text-sm text-ostrel-ink focus:border-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
          >
            <option value="">— none</option>
            {locations.map((l) => (
              <option key={l.uid} value={l.uid}>
                {l.label}
              </option>
            ))}
          </select>
        </label>
      )}
      {showDoorLocations && (
        <label className="flex flex-col gap-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
            {doorLocationRequired ? 'Location (required)' : 'Location (optional — defaults to device placement)'}
          </span>
          {doorLocationHint && (
            <p className="tnum text-[11px] text-ostrel-ink-muted">
              Suggested:{' '}
              <span className="text-ostrel-gold">
                {locationLabels.get(doorLocationHint.canonical_uid) ?? doorLocationHint.canonical_uid}
              </span>{' '}
              · {Math.round(doorLocationHint.score * 100)}% match
            </p>
          )}
          <select
            value={doorLocationUid}
            onChange={(e) => setDoorLocationUid(e.target.value)}
            aria-label="Door location"
            className="rounded-sm border border-ostrel-line bg-ostrel-card px-3 py-2 text-sm text-ostrel-ink focus:border-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
          >
            <option value="">— none</option>
            {locations.map((l) => (
              <option key={l.uid} value={l.uid}>
                {l.label}
              </option>
            ))}
          </select>
          {/* The Door's name is derived from the device name backend-side —
              the kind is selectable (auto-detect is the default). */}
          <span className="text-[10px] text-ostrel-ink-muted">The door takes the device name.</span>
        </label>
      )}
      {item.kind === 'door' && (
        <label className="flex flex-col gap-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
            Kind
          </span>
          <select
            value={doorKindSel}
            onChange={(e) => setDoorKindSel(e.target.value as DoorKind | '')}
            aria-label="Door kind"
            className="rounded-sm border border-ostrel-line bg-ostrel-card px-3 py-2 text-sm text-ostrel-ink focus:border-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
          >
            <option value="">Auto-detect ({DOOR_KIND_LABELS[candidateDoorKind(item)]})</option>
            {(Object.entries(DOOR_KIND_LABELS) as [DoorKind, string][]).map(([value, kindLabel]) => (
              <option key={value} value={value}>
                {kindLabel}
              </option>
            ))}
          </select>
        </label>
      )}
      {showScope && rotation && (
        <label className="flex items-center gap-2 text-xs text-ostrel-ink">
          <input
            type="checkbox"
            checked={asSeries}
            onChange={(e) => setAsSeries(e.target.checked)}
            className="accent-ostrel-gold"
          />
          Create as rotating series — {CADENCE_LABELS[rotation.cadence] ?? rotation.cadence} (
          {rotation.period} is the current period)
        </label>
      )}
      {showScope && (
        <ScopePicker
          locations={scopeLocations}
          doors={scopeDoors}
          onLocations={setScopeLocations}
          onDoors={setScopeDoors}
        />
      )}
      {decide.error && (
        <p className="rounded-sm border border-ostrel-danger/40 bg-ostrel-danger/10 px-3 py-2 text-xs text-ostrel-danger">
          {errDetail(decide.error)}
        </p>
      )}
      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onDone}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="navy"
          size="sm"
          disabled={decide.isPending || (sighted && !personUid) || (doorLocationRequired && !doorLocationUid)}
        >
          {decide.isPending ? 'Creating…' : sighted ? 'Convert' : 'Create'}
        </Button>
      </div>
    </form>
  )
}
